import { useState, useRef, useEffect } from 'react';
import { useTranslation } from '@parentpath/i18n';
import { ArrowLeft, Check, X, ChevronLeft, ChevronRight, Loader2, ImageOff, AlertCircle, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePrices } from '@/features/subscription/hooks/usePrices';
import { usePremiumContent } from '@/features/subscription/hooks/usePremiumContent';
import { useCreateCheckout } from '@/features/subscription/hooks/useSubscription';
import { usePurchaseIAP, useRestorePurchases, REVENUECAT_PRODUCTS } from '@/features/subscription/hooks/useRevenueCat';
import { STRIPE_PRICES } from '@/lib/stripe';
import { isNative, isIOS } from '@/lib/capacitor';

interface PremiumStepProps {
  onNext: () => void; // Continue without premium
  onBack: () => void;
  onPromoCode: () => void; // Navigate to promo code step
}

type PlanType = 'monthly' | 'sixMonth' | 'yearly';

// Placeholder component for missing images
function ImagePlaceholder({ t }: { t: (key: string) => string }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-white/5 rounded-[15px] border-2 border-dashed border-white/20">
      <ImageOff className="w-12 h-12 text-white/30 mb-2" />
      <span className="text-sm text-white/30">{t('premium.imageMissing')}</span>
    </div>
  );
}

// Slide image with error handling
function SlideImage({ src, alt, t }: { src: string; alt: string; t: (key: string) => string }) {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check if URL is empty or invalid
  const isValidUrl = src && src.trim() !== '';

  if (!isValidUrl || hasError) {
    return <ImagePlaceholder t={t} />;
  }

  return (
    <>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/5 rounded-[15px]">
          <Loader2 className="w-8 h-8 animate-spin text-white/30" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={cn(
          'w-full h-full object-cover object-center rounded-[15px]',
          isLoading && 'opacity-0'
        )}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setHasError(true);
        }}
      />
    </>
  );
}

export function PremiumStep({ onNext, onBack, onPromoCode }: PremiumStepProps) {
  const { t } = useTranslation('onboarding');
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('sixMonth');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [errorTimeLeft, setErrorTimeLeft] = useState(0);
  const slideContainerRef = useRef<HTMLDivElement>(null);

  const prices = usePrices();
  const { features, slides } = usePremiumContent();
  const createCheckout = useCreateCheckout();
  const purchaseIAP = usePurchaseIAP();
  const restorePurchases = useRestorePurchases();

  const isPurchasing = createCheckout.isPending || purchaseIAP.isPending;

  // Auto-advance slideshow
  useEffect(() => {
    if (slides.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [slides.length]);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (purchaseError) {
      setErrorTimeLeft(5);
      const interval = setInterval(() => {
        setErrorTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setPurchaseError(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [purchaseError]);

  // Clear error when changing plan
  const handlePlanChange = (plan: PlanType) => {
    setPurchaseError(null);
    setSelectedPlan(plan);
  };

  // Touch handling for slideshow
  const touchStartX = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;

    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentSlide < slides.length - 1) {
        setCurrentSlide(prev => prev + 1);
      } else if (diff < 0 && currentSlide > 0) {
        setCurrentSlide(prev => prev - 1);
      }
    }
  };

  const handleCheckout = async () => {
    setPurchaseError(null);

    if (isNative) {
      // Native: Use RevenueCat IAP
      try {
        const productId = selectedPlan === 'monthly'
          ? REVENUECAT_PRODUCTS.MONTHLY
          : selectedPlan === 'sixMonth'
            ? REVENUECAT_PRODUCTS.SIX_MONTH
            : REVENUECAT_PRODUCTS.YEARLY;

        await purchaseIAP.mutateAsync(productId);
        // On success, proceed to next step
        onNext();
      } catch (error) {
        if (error instanceof Error && error.message === 'PURCHASE_CANCELLED') {
          // User cancelled - no error message needed
          return;
        }
        // Log detailed error info for debugging
        console.error('IAP Error:', error instanceof Error ? error.message : 'Unknown error');
        if (error && typeof error === 'object') {
          console.error('IAP Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
        }
        setPurchaseError(t('premium.purchaseError'));
      }
    } else {
      // Web: Use Stripe
      const priceId = selectedPlan === 'monthly'
        ? STRIPE_PRICES.PREMIUM_MONTHLY
        : selectedPlan === 'sixMonth'
          ? STRIPE_PRICES.PREMIUM_6MONTH
          : STRIPE_PRICES.PREMIUM_1YEAR;

      try {
        const result = await createCheckout.mutateAsync({
          priceId,
          successUrl: `${window.location.origin}/onboarding?step=completion&premium=success`,
          cancelUrl: `${window.location.origin}/onboarding?step=premium`,
        });

        if (result.url) {
          window.location.href = result.url;
        }
      } catch (error) {
        console.error('Checkout error:', error);
        setPurchaseError(t('premium.checkoutError'));
      }
    }
  };

  // Handle restore purchases (native only)
  const handleRestorePurchases = async () => {
    setPurchaseError(null);
    try {
      const result = await restorePurchases.mutateAsync();
      if (result.hasPremium) {
        // User has premium, proceed to next step
        onNext();
      } else {
        setPurchaseError(t('premium.restoreEmpty'));
      }
    } catch (error) {
      console.error('Restore Error:', error);
      setPurchaseError(t('premium.restoreError'));
    }
  };

  // Render feature value
  const renderFeatureValue = (value: boolean | string, isPremium: boolean) => {
    if (typeof value === 'string') {
      return <span className={cn('text-sm', isPremium ? 'text-pp-orange font-medium' : 'text-white/60')}>{value}</span>;
    }
    return value ? (
      <Check className={cn('w-5 h-5', isPremium ? 'text-pp-orange' : 'text-green-400')} />
    ) : (
      <X className="w-5 h-5 text-white/30" />
    );
  };

  return (
    <div className="flex flex-col -m-6">
      {/* Back Button */}
      <div className="px-6 pt-6 pb-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white/60 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </button>
      </div>

      {/* Title */}
      <div className="px-6 pb-4">
        <h1 className="text-2xl font-bold text-white text-center">
          {t('premium.title')}
        </h1>
      </div>

      {/* Slideshow */}
      <div className="relative mb-4 px-4">
        <div
          ref={slideContainerRef}
          className="relative w-full overflow-hidden rounded-[15px] mx-auto"
          style={{ aspectRatio: '16/9', maxHeight: '200px' }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {slides.length > 0 ? (
            <div
              className="flex transition-transform duration-300 h-full"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {slides.map((slide) => (
                <div
                  key={slide.id}
                  className="w-full h-full flex-shrink-0 relative"
                >
                  <SlideImage src={slide.imageUrl} alt={slide.altText} t={t} />
                </div>
              ))}
            </div>
          ) : (
            /* Placeholder when no slides configured */
            <ImagePlaceholder t={t} />
          )}

          {/* Navigation Arrows */}
          {slides.length > 1 && (
            <>
              <button
                onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white/70 hover:text-white"
                disabled={currentSlide === 0}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setCurrentSlide(prev => Math.min(slides.length - 1, prev + 1))}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white/70 hover:text-white"
                disabled={currentSlide === slides.length - 1}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        {/* Slide Indicators */}
        {slides.length > 1 && (
          <div className="flex justify-center gap-2 mt-3">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={cn(
                  'w-2 h-2 rounded-full transition-colors',
                  index === currentSlide ? 'bg-pp-orange' : 'bg-white/20'
                )}
              />
            ))}
          </div>
        )}
      </div>

      {/* Glass Container */}
      <div className="px-4 pb-6">
        <div className="glass-effect rounded-[15px] border border-white/15 p-5">
          {/* Plan Selection - 3 Options */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {/* Monthly */}
            <button
              onClick={() => handlePlanChange('monthly')}
              className={cn(
                'p-3 rounded-[10px] border-2 text-center transition-all',
                selectedPlan === 'monthly'
                  ? 'border-pp-orange bg-pp-orange/20'
                  : 'border-white/20 bg-white/5 hover:border-white/30'
              )}
            >
              <div className="text-xs text-white/50 mb-1">{t('premium.monthly')}</div>
              <div className="text-lg font-bold text-white">
                {prices.monthly?.priceFormatted || '3,99 €'}
              </div>
              <div className="text-xs text-white/50">{t('premium.perMonth')}</div>
            </button>

            {/* 6-Month - Recommended */}
            <button
              onClick={() => handlePlanChange('sixMonth')}
              className={cn(
                'p-3 rounded-[10px] border-2 text-center transition-all relative',
                selectedPlan === 'sixMonth'
                  ? 'border-pp-orange bg-pp-orange/20'
                  : 'border-white/20 bg-white/5 hover:border-white/30'
              )}
            >
              {prices.sixMonth?.savingsPercent && prices.sixMonth.savingsPercent > 0 && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap">
                  -{prices.sixMonth.savingsPercent}%
                </div>
              )}
              <div className="text-xs text-white/50 mb-1">{t('premium.sixMonths')}</div>
              <div className="text-lg font-bold text-white">
                {prices.sixMonth?.priceFormatted || '14,99 €'}
              </div>
              <div className="text-xs text-green-400">{t('premium.freeTrial')}</div>
            </button>

            {/* 1 Jahr */}
            <button
              onClick={() => handlePlanChange('yearly')}
              className={cn(
                'p-3 rounded-[10px] border-2 text-center transition-all relative',
                selectedPlan === 'yearly'
                  ? 'border-pp-orange bg-pp-orange/20'
                  : 'border-white/20 bg-white/5 hover:border-white/30'
              )}
            >
              {prices.yearly?.savingsPercent && prices.yearly.savingsPercent > 0 && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap">
                  -{prices.yearly.savingsPercent}%
                </div>
              )}
              <div className="text-xs text-white/50 mb-1">{t('premium.oneYear')}</div>
              <div className="text-lg font-bold text-white">
                {prices.yearly?.priceFormatted || '23,99 €'}
              </div>
              <div className="text-xs text-green-400">{t('premium.freeTrial')}</div>
            </button>
          </div>

          {/* Selected Plan Info */}
          <div className="text-center mb-4 text-sm text-white/60">
            {selectedPlan === 'monthly' && t('premium.monthlyInfo')}
            {selectedPlan === 'sixMonth' && t('premium.trialInfo', { price: prices.sixMonth?.perMonthFormatted || '2,50 €' })}
            {selectedPlan === 'yearly' && t('premium.trialInfo', { price: prices.yearly?.perMonthFormatted || '2,00 €' })}
          </div>

          {/* Feature Comparison - 2 Column Layout */}
          <div className="bg-white/5 rounded-[10px] overflow-hidden mb-5">
            {/* Table Header */}
            <div className="grid grid-cols-2 gap-2 px-3 py-2 border-b border-white/10">
              <div className="text-xs font-medium text-white/50 text-center">{t('premium.free')}</div>
              <div className="text-xs font-medium text-pp-orange text-center">{t('premium.premiumLabel')}</div>
            </div>
            {/* Feature Rows */}
            <div className="divide-y divide-white/10">
              {features.map((feature) => (
                <div key={feature.id} className="p-3">
                  {/* Feature Name centered as sub-heading */}
                  <div className="text-sm font-medium text-white/90 mb-2 text-center">
                    {feature.name}
                  </div>
                  {/* Free vs Premium Values */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex justify-center">
                      {renderFeatureValue(feature.free, false)}
                    </div>
                    <div className="flex justify-center">
                      {renderFeatureValue(feature.premium, true)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Error message */}
          {purchaseError && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-[10px] overflow-hidden mb-4">
              <div className="p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-300">{purchaseError}</p>
                  </div>
                  <button
                    onClick={() => setPurchaseError(null)}
                    className="text-red-400/70 hover:text-red-300 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {/* Progress bar */}
              <div className="h-1 bg-red-500/20">
                <div
                  className="h-full bg-red-400 transition-all duration-1000 ease-linear"
                  style={{ width: `${(errorTimeLeft / 5) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="space-y-3">
            {/* Primary: Premium starten */}
            <button
              onClick={handleCheckout}
              disabled={isPurchasing}
              className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors disabled:opacity-50 flex items-center justify-center"
            >
              {isPurchasing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t('premium.start')
              )}
            </button>

            {/* Restore Purchases (native only) */}
            {isNative && (
              <button
                onClick={handleRestorePurchases}
                disabled={restorePurchases.isPending}
                className="w-full py-3 bg-transparent border-2 border-white/20 rounded-[10px] text-white font-medium hover:border-white/40 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {restorePurchases.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4" />
                )}
                {t('premium.restore')}
              </button>
            )}

            {/* Secondary: Promo-Code */}
            <button
              onClick={onPromoCode}
              className="w-full py-3 bg-transparent border-2 border-white/20 rounded-[10px] text-white font-medium hover:border-white/40 transition-colors"
            >
              {t('premium.promoCode')}
            </button>

            {/* Tertiary: Skip */}
            <button
              onClick={onNext}
              className="w-full py-2 text-white/50 hover:text-white/70 text-sm transition-colors"
            >
              {t('premium.skip')}
            </button>

            {/* Payment Info */}
            <div className="text-xs text-white/40 text-center space-y-2">
              <p>
                {isNative
                  ? t('premium.paymentProvider', { provider: isIOS ? 'Apple' : 'Google Play' })
                  : t('premium.paymentProvider', { provider: 'Stripe' })
                }
              </p>
              <p>
                {t('premium.termsText')}{' '}
                <a href="/terms" className="text-pp-orange hover:underline">
                  {t('premium.termsLink')}
                </a>{' '}
                {t('premium.and')}{' '}
                <a href="/privacy" className="text-pp-orange hover:underline">
                  {t('premium.privacyLink')}
                </a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
